// Fleet → Admission queue.
//
// NEW IN V2. A device that installs GaliGali OS self-registers with AWS IoT
// and lands with requires_admin_review = true until a human admits it.
// Measured 2026-08-28: 50 of 56 field devices sat in that state, and
// /api/dashboard/alerts had been computing the queue all along —
// dashboardService.getAlerts() exists, and no classic screen ever called it.
//
// At 56 devices you can hold this in your head. At 3,000 you cannot, which
// is why this is the front of the Fleet domain rather than a dashboard tile.

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHead, Tiles, DataTable, Pill, Loading, ErrorState, Empty, Button } from '../../components/ui';
import useApi from '../../lib/useApi';
import { dashboardService } from '../../../services/api';

const fmt = (t) => (t ? new Date(t).toLocaleString(undefined,
  { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : null);

const ageDays = (t) => (t ? Math.floor((Date.now() - new Date(t).getTime()) / 86400000) : null);

export default function Admission() {
  const [showTest, setShowTest] = useState(false);
  const { data, error, loading, reload } = useApi(() => dashboardService.getAlerts(), []);

  if (loading) return <><Head /><Loading what="the admission queue" /></>;
  if (error)   return <><Head /><ErrorState error={error} onRetry={reload} /></>;

  const all = Array.isArray(data?.data) ? data.data : [];
  // Test rows share this queue with real self-registrations — 20 of the 42
  // open on 2026-08-28 were one 2026-03-02 seeding run. Showing them mixed in
  // makes the queue read as twice its real size, so real devices lead and
  // test rows are counted separately.
  const rows = showTest ? all : all.filter((r) => !r.is_test_device);
  const testCount = all.filter((r) => r.is_test_device).length;

  if (!rows.length) {
    return (
      <>
        <Head onRefresh={reload} />
        <Empty title="Nothing waiting">
          Every registered device has been reviewed. New self-registrations will appear here.
        </Empty>
      </>
    );
  }

  const stale = rows.filter((r) => (ageDays(r.registered_at) ?? 0) >= 7).length;
  const noBootstrap = rows.filter((r) => !r.bootstrap_auth_status || r.bootstrap_auth_status === 'missing').length;

  return (
    <>
      <Head onRefresh={reload} />
      <Tiles items={[
        { label: 'Awaiting review', value: rows.length, tone: 'bad', foot: showTest ? 'incl. test rows' : 'real devices' },
        { label: 'Waiting 7d+',     value: stale, tone: stale ? 'warn' : undefined },
        { label: 'No bootstrap auth', value: noBootstrap, foot: 'never presented a trusted CA' },
        { label: 'Test rows',       value: testCount, foot: showTest ? 'shown' : 'hidden' },
      ]} />

      <DataTable
        rows={rows}
        getKey={(r) => r.id}
        filterOn={['thing_name', 'device_model', 'registration_status']}
        filterPlaceholder="Filter by thing name or model…"
        initialSort={{ key: 'registered_at', dir: 'asc' }}
        right={testCount ? (
          <Button onClick={() => setShowTest((v) => !v)}>
            {showTest ? 'Hide test rows' : `Show ${testCount} test rows`}
          </Button>
        ) : null}
        columns={[
          { key: 'thing_name', label: 'Thing name', mono: true,
            render: (r) => <Link to={`/v2/fleet/device/${encodeURIComponent(r.thing_name)}`} style={{ color: 'var(--iris-text)' }}>{r.thing_name}</Link> },
          { key: 'device_model', label: 'Model' },
          { key: 'registration_status', label: 'Registration',
            render: (r) => <Pill tone={r.registration_status === 'registered' ? 'grow' : 'warn'}>{r.registration_status || 'unknown'}</Pill> },
          { key: 'bootstrap_auth_status', label: 'Bootstrap auth',
            render: (r) => <Pill tone={r.bootstrap_auth_status && r.bootstrap_auth_status !== 'missing' ? 'grow' : 'bad'}>
              {r.bootstrap_auth_status || 'missing'}</Pill> },
          { key: 'registered_at', label: 'Registered', render: (r) => fmt(r.registered_at) },
          { key: 'age', label: 'Waiting', align: 'num',
            sort: (r) => -(ageDays(r.registered_at) ?? 0),
            render: (r) => { const d = ageDays(r.registered_at); return d == null ? null : `${d}d`; } },
        ]}
      />

      <p style={{ marginTop: 'var(--s4)', fontSize: 13, color: 'var(--ink-mute)', maxWidth: '78ch' }}>
        Admitting a device is not yet wired here. The decision rule is an open question —
        per-device, bulk approve-by-batch, or automatic when a hardware identifier matches an
        inventory row — and it determines whether this stays a queue or becomes a rule engine.
        Until then the classic device page remains the place to act.
      </p>
    </>
  );
}

const Head = ({ onRefresh }) => (
  <PageHead
    title="Admission queue"
    sub="Devices that installed GaliGali OS, self-registered, and are waiting for a human to admit them. The server has computed this queue all along; no screen showed it until now."
    actions={onRefresh ? <Button onClick={onRefresh}>Refresh</Button> : null}
  />
);
